/**
 * `gameFlag` resolution for `VIFRulesMulDiv`.
 *
 * Extracted verbatim from `VIFRulesMulDiv.computeGameFlag` so the CreateJS
 * game and the DOM board resolve variants identically. Unlike
 * `rulesAddSubFlag.ts`'s table, `moduleId` values here never overlap
 * add/sub's - MulDiv has its own six moduleIds (172, 180, 168, 173, 181, 169).
 *
 * Flags 0-9 are multiplication, 10-16 subtraction... no - division
 * (`computeOperation` is `gameFlag <= 9 ? MUL : DIV`). There is no flag 9.
 */

/** `${moduleId}:${moduleVariant}` -> gameFlag. */
const FLAG_BY_MODULE: Readonly<Record<string, number>> = {
  '172:0': 0, // zero-one rule
  '172:1': 2, // mirror rule
  '180:0': 7, // Auditory Memory (Part 1)
  '180:1': 8, // Auditory Memory (Part 2)
  '180:2': 6, // Auditory Memory (all 36 facts)
  '180:3': 4, // tens by ones
  '180:4': 3, // double digits by single digits
  '180:5': 1, // ten by tens
  '168:1': 5, // double digits by double digits
  '173:0': 10, // Divide zero-one rule
  '173:1': 12, // Divide Loose Mirror Rule
  '181:0': 16, // Divide 36 Facts Game
  '181:1': 14, // Multiply Single Digits by Tens (reports DIV - see computeOperation)
  '181:2': 13, // Divide Triple Digits by Single Digits
  '181:3': 11, // Multiply Double Digits by Tens (reports DIV - see computeOperation)
  '169:1': 15, // Dividing Multi Digits by Double Digits
};

/**
 * Resolves the rules mul/div `gameFlag`.
 *
 * `fallback` is kept separate from `moduleVariant` on purpose: the legacy
 * code seeds `let gameFlag = moduleVariant` from the *parameter* while every
 * lookup branch tests `gameFrame.moduleId`. Those two are normally equal to
 * the resolved flag only when unmapped, but collapsing them would silently
 * change behaviour where they are not.
 */
export function resolveRulesMulDivGameFlag(
  moduleId: number,
  moduleVariant: number,
  fallback: number = moduleVariant,
): number {
  return lookupRulesMulDivGameFlag(moduleId, moduleVariant) ?? fallback;
}

/**
 * Strict lookup: `null` when the module/variant pair is not in the table.
 *
 * Routing decisions must use this rather than `resolveRulesMulDivGameFlag`.
 * The lenient fallback exists only to preserve legacy canvas behaviour, and
 * it maps any unmapped variant onto whatever flag happens to equal the raw
 * variant number - which would quietly route unrelated modules to the DOM
 * board.
 */
export function lookupRulesMulDivGameFlag(
  moduleId: number,
  moduleVariant: number,
): number | null {
  return FLAG_BY_MODULE[`${moduleId}:${moduleVariant}`] ?? null;
}
